import React, { useState } from 'react';
import { 
  Key, ShieldAlert, Eye, EyeOff, Lock, CheckCircle2, 
  RefreshCw, AlertCircle, Copy, Check, Send, X, ShieldCheck
} from 'lucide-react';
import { UserProfile } from '../../types';
import { useWBL } from '../../context/WBLContext';

interface PasswordManagementModalProps {
  user: UserProfile;
  isOpen: boolean;
  onClose: () => void;
}

export const PasswordManagementModal: React.FC<PasswordManagementModalProps> = ({
  user,
  isOpen,
  onClose
}) => {
  const { updateUserProfile, activeUser, adminResetUserPassword } = useWBL();

  const [activeTab, setActiveTab] = useState<'set' | 'reset' | 'security'>('set');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [forceChangeOnNextLogin, setForceChangeOnNextLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const generateRandomPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%&*';
    let pass = 'WBL#';
    for (let i = 0; i < 8; i++) {
      pass += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewPassword(pass);
    setShowPassword(true);
    setSuccessMsg('');
    setErrorMsg('');
  };

  const handleCopy = () => {
    if (!newPassword) return;
    navigator.clipboard.writeText(newPassword);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPassword || newPassword.length < 6) {
      setErrorMsg('Password must be at least 6 characters long.');
      return;
    }

    setLoading(true);
    setErrorMsg('');
    setSuccessMsg('');

    try {
      if (adminResetUserPassword) {
        await adminResetUserPassword(user.uid, newPassword, forceChangeOnNextLogin);
      } else {
        await updateUserProfile(user.uid, {
          requiresPasswordChange: forceChangeOnNextLogin,
          lastPasswordReset: new Date().toISOString(),
          resetByAdmin: activeUser?.email || 'Administrator'
        });
      }

      setSuccessMsg(`Password successfully set for ${user.name || user.email}!`);
      setTimeout(() => {
        setSuccessMsg('');
      }, 4000);
    } catch (err: any) {
      console.error('Error setting user password:', err);
      setErrorMsg(err?.message || 'Failed to set user password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleForceReset = async () => {
    setLoading(true);
    setErrorMsg('');
    setSuccessMsg('');
    try {
      const nextState = !user.requiresPasswordChange;
      await updateUserProfile(user.uid, {
        requiresPasswordChange: nextState
      });
      setSuccessMsg(nextState 
        ? `Flagged ${user.name}: Password change required on next login.` 
        : `Cleared password change requirement for ${user.name}.`
      );
    } catch (err: any) {
      setErrorMsg('Failed to update password reset requirement.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-fade-in">
      <div className="bg-white rounded-3xl border border-slate-200 shadow-2xl max-w-lg w-full overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-[#011d3c] text-white p-6 relative">
          <button 
            type="button"
            onClick={onClose}
            className="absolute right-5 top-5 text-slate-400 hover:text-white transition-colors p-1"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="flex items-center space-x-3">
            <div className="p-2.5 bg-[#0094c8]/20 border border-[#0094c8]/40 rounded-xl text-[#0094c8]">
              <Key className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-lg font-black uppercase tracking-tight text-white">Console Password Management</h3>
              <p className="text-xs font-semibold text-slate-300">{user.name} ({user.email})</p>
            </div>
          </div>
        </div>

        {/* Tab Selection */}
        <div className="flex border-b border-slate-100 bg-slate-50/80 px-6 pt-3 gap-2">
          <button
            type="button"
            onClick={() => { setActiveTab('set'); setErrorMsg(''); setSuccessMsg(''); }}
            className={`pb-3 px-3 text-xs font-black uppercase tracking-wider border-b-2 transition-all cursor-pointer ${
              activeTab === 'set'
                ? 'border-[#0094c8] text-[#0094c8]'
                : 'border-transparent text-slate-400 hover:text-slate-600'
            }`}
          >
            Set Password
          </button>
          <button
            type="button"
            onClick={() => { setActiveTab('reset'); setErrorMsg(''); setSuccessMsg(''); }}
            className={`pb-3 px-3 text-xs font-black uppercase tracking-wider border-b-2 transition-all cursor-pointer ${
              activeTab === 'reset'
                ? 'border-[#0094c8] text-[#0094c8]'
                : 'border-transparent text-slate-400 hover:text-slate-600'
            }`}
          >
            Reset Flags
          </button>
          <button
            type="button"
            onClick={() => { setActiveTab('security'); setErrorMsg(''); setSuccessMsg(''); }}
            className={`pb-3 px-3 text-xs font-black uppercase tracking-wider border-b-2 transition-all cursor-pointer ${
              activeTab === 'security'
                ? 'border-[#0094c8] text-[#0094c8]'
                : 'border-transparent text-slate-400 hover:text-slate-600'
            }`}
          >
            Security Q&A
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 space-y-5 flex-1 overflow-y-auto">
          {errorMsg && (
            <div className="flex items-center space-x-2 bg-rose-50 border border-rose-200 text-rose-700 p-3 rounded-xl text-xs font-bold animate-shake">
              <AlertCircle className="w-4 h-4 text-rose-500 shrink-0" />
              <span>{errorMsg}</span>
            </div>
          )}

          {successMsg && (
            <div className="flex items-center space-x-2 bg-emerald-50 border border-emerald-200 text-emerald-800 p-3 rounded-xl text-xs font-bold animate-fade-in">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
              <span>{successMsg}</span>
            </div>
          )}

          {/* TAB 1: SET PASSWORD */}
          {activeTab === 'set' && (
            <form onSubmit={handleSetPassword} className="space-y-4">
              <div>
                <label className="block text-[10px] font-black uppercase text-slate-400 tracking-wider mb-1.5">
                  Set New Password For User
                </label>
                <div className="relative flex items-center">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new secure password..."
                    className="w-full pl-3 pr-20 py-2.5 border border-slate-200 rounded-xl text-xs font-mono font-bold text-slate-800 focus:ring-2 focus:ring-[#0094c8] outline-none bg-white"
                  />
                  <div className="absolute right-2 flex items-center space-x-1">
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="text-slate-400 hover:text-slate-600 p-1.5"
                      title={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                    {newPassword && (
                      <button
                        type="button"
                        onClick={handleCopy}
                        className="text-slate-400 hover:text-[#0094c8] p-1.5"
                        title="Copy password"
                      >
                        {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={generateRandomPassword}
                  className="inline-flex items-center space-x-1.5 text-[11px] font-black uppercase tracking-wider text-[#0094c8] hover:text-[#007ba6] bg-sky-50 px-3 py-1.5 rounded-lg border border-sky-100 transition-all cursor-pointer"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  <span>Auto-Generate Temp Password</span>
                </button>
              </div>

              <div className="pt-2 border-t border-slate-100">
                <label className="flex items-center space-x-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={forceChangeOnNextLogin}
                    onChange={(e) => setForceChangeOnNextLogin(e.target.checked)}
                    className="rounded border-slate-300 text-[#0094c8] focus:ring-[#0094c8] w-4 h-4"
                  />
                  <span className="text-xs font-extrabold text-slate-700">
                    Require user to change password on next login
                  </span>
                </label>
              </div>

              <button
                type="submit"
                disabled={loading || !newPassword}
                className="w-full bg-[#0094c8] hover:bg-[#007ba6] disabled:opacity-50 text-white font-black text-xs uppercase tracking-wider py-3 rounded-xl transition-all shadow-md cursor-pointer flex items-center justify-center space-x-2"
              >
                {loading ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Lock className="w-4 h-4" />
                    <span>Apply New Password</span>
                  </>
                )}
              </button>
            </form>
          )}

          {/* TAB 2: RESET FLAGS */}
          {activeTab === 'reset' && (
            <div className="space-y-4">
              <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200/80 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-extrabold text-slate-600">Password Change Status:</span>
                  <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider border ${
                    user.requiresPasswordChange
                      ? 'bg-amber-50 text-amber-700 border-amber-200'
                      : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                  }`}>
                    {user.requiresPasswordChange ? 'Change Required On Next Login' : 'Password Active'}
                  </span>
                </div>

                {user.lastPasswordReset && (
                  <div className="flex items-center justify-between text-xs font-semibold text-slate-500 pt-2 border-t border-slate-200/60">
                    <span>Last Password Reset:</span>
                    <span className="font-mono text-slate-800">{new Date(user.lastPasswordReset).toLocaleString()}</span>
                  </div>
                )}

                {user.resetByAdmin && (
                  <div className="flex items-center justify-between text-xs font-semibold text-slate-500">
                    <span>Reset By Admin:</span>
                    <span className="font-mono text-slate-800">{user.resetByAdmin}</span>
                  </div>
                )}
              </div>

              <button
                type="button"
                onClick={handleToggleForceReset}
                disabled={loading}
                className={`w-full py-2.5 px-4 rounded-xl text-xs font-black uppercase tracking-wider transition-all border cursor-pointer flex items-center justify-center space-x-2 ${
                  user.requiresPasswordChange
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                    : 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100'
                }`}
              >
                {loading ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : user.requiresPasswordChange ? (
                  <>
                    <ShieldCheck className="w-4 h-4" />
                    <span>Clear Forced Password Reset Requirement</span>
                  </>
                ) : (
                  <>
                    <ShieldAlert className="w-4 h-4" />
                    <span>Force Password Reset On Next Login</span>
                  </>
                )}
              </button>
            </div>
          )}

          {/* TAB 3: SECURITY Q&A / WHY PASSWORDS CANNOT BE VIEWED */}
          {activeTab === 'security' && (
            <div className="space-y-3 bg-amber-50/60 border border-amber-200/80 rounded-2xl p-4 text-xs text-amber-900 leading-relaxed">
              <div className="flex items-center space-x-2 font-black uppercase tracking-wider text-amber-950 pb-2 border-b border-amber-200/60">
                <ShieldAlert className="w-5 h-5 text-amber-600 shrink-0" />
                <span>Is Viewing Stored Passwords A Bad Idea?</span>
              </div>

              <p className="font-semibold text-amber-900">
                <strong className="font-black text-amber-950">Yes, viewing stored passwords is a major security flaw and anti-pattern.</strong> Here is why plain-text passwords cannot and should never be readable by anyone (including console admins):
              </p>

              <ul className="space-y-2 list-disc pl-4 font-medium text-amber-900/90 text-[11px]">
                <li>
                  <strong className="font-black text-amber-950">One-Way Cryptographic Hashing:</strong> Secure identity platforms (Firebase Auth, bcrypt, Argon2) store passwords using irreversible salt + hash algorithms. Plain-text passwords simply do not exist in the database.
                </li>
                <li>
                  <strong className="font-black text-amber-950">Compliance & Privacy Regulations:</strong> FERPA, SOC 2, HIPAA, and GDPR strictly forbid storing or displaying plain-text user passwords.
                </li>
                <li>
                  <strong className="font-black text-amber-950">Credential Reuse Risks:</strong> Many students and employees reuse passwords across personal accounts. Readable passwords create extreme liability if an admin account or console log is compromised.
                </li>
                <li>
                  <strong className="font-black text-amber-950">Industry Best Practice:</strong> Always use <span className="font-bold">Password Reset</span> or <span className="font-bold">Set New Temporary Password</span> instead of attempting to view existing user credentials.
                </li>
              </ul>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 bg-slate-50 border-t border-slate-100 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-xl text-xs font-black uppercase tracking-wider transition-colors cursor-pointer"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
